import React from 'react';

type Props = {
  children: React.ReactNode;
  /** Label for console / on-screen diagnostics. */
  label?: string;
};

type State = {
  error: Error | null;
};

/**
 * Surfaces render/effect crashes instead of emptying #root (production React
 * otherwise unmounts the whole createRoot tree with no UI).
 */
export class OroborosErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('[OroborosErrorBoundary]', this.props.label || 'oroboros', error, info);
    try {
      window.localStorage.setItem(
        'oro_last_boundary_error',
        JSON.stringify({
          at: new Date().toISOString(),
          label: this.props.label || 'oroboros',
          message: String(error?.message || ''),
          stack: String(error?.stack || '')
            .split('\n')
            .slice(0, 12)
            .join('\n'),
          componentStack: String(info?.componentStack || '')
            .split('\n')
            .slice(0, 12)
            .join('\n'),
        }),
      );
    } catch {
      /* ignore */
    }
  }

  private handleRetry = (): void => {
    this.setState({ error: null });
  };

  render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const message = String(error.message || 'Unknown error').trim() || 'Unknown error';
    const stackHead = String(error.stack || '')
      .split('\n')
      .slice(0, 8)
      .join('\n')
      .trim();

    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          margin: 0,
          padding: '24px 20px',
          boxSizing: 'border-box',
          background: '#050208',
          color: '#f2eef8',
          fontFamily:
            'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
        }}
      >
        <h1 style={{ fontSize: 18, margin: '0 0 8px' }}>Oroboros hit an error</h1>
        <p style={{ margin: '0 0 16px', opacity: 0.85, lineHeight: 1.45 }}>
          The terminal crashed while loading. Details below — reload or retry after reporting.
        </p>
        <pre
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            background: '#12081c',
            border: '1px solid #3a2a4e',
            borderRadius: 8,
            padding: 12,
            fontSize: 12,
            lineHeight: 1.4,
            color: '#ffb4b4',
            margin: '0 0 16px',
          }}
        >
          {stackHead && stackHead !== message ? `${message}\n\n${stackHead}` : message}
        </pre>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={this.handleRetry}
            style={{
              cursor: 'pointer',
              border: '1px solid #6b4f8a',
              background: '#2a1838',
              color: '#f2eef8',
              borderRadius: 8,
              padding: '8px 14px',
              fontSize: 14,
            }}
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              cursor: 'pointer',
              border: '1px solid #6b4f8a',
              background: '#2a1838',
              color: '#f2eef8',
              borderRadius: 8,
              padding: '8px 14px',
              fontSize: 14,
            }}
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}

export default OroborosErrorBoundary;
